/**
 * Listing Data Orchestrator
 *
 * Coordinates all data fetching for product listing pages.
 * All external service calls go through BFF API endpoints:
 * - Products: /api/products/search (Algolia)
 * - Facets: /api/facets/[locale] (Algolia, ISR cached 1 hour)
 * - CMS: /api/cms/listing (Contentful, ISR cached 5 mins)
 *
 * Caching:
 * - Uses React cache() for request deduplication within a single render
 * - BFF endpoints handle ISR caching where appropriate
 */

import type { ProductCardData } from '@/components/brand';
import {
  fetchStores,
  getAlgoliaDebugEntries,
  getCachedFacetsDeduped,
  getLocaleCode,
  getProductsByIds,
  runWithAlgoliaDebugCollection,
  searchByProductType,
  searchProductsInternal,
  type FacetProductType,
  type SearchProductType,
  type StrapHit,
} from '@/lib/algolia';
import type {
  CMSCollectionGroup,
  CMSContentItem,
  CMSPlaceholderStores,
  CMSProductCard,
  CMSTrendingProducts,
} from '@/lib/cms';
import { fetchListingPage } from '@/lib/cms/bffClient';
import { filterContent } from '@/lib/contentful/api';
import { buildStrapUrl, buildWatchUrl } from '@/lib/product';
import { getProductBadge } from '@/lib/product/marketing-status';
import { getProductPlaceholder } from '@/lib/product/placeholders';
import { fetchStrapVariantCtas, resolveStrapListingCta } from '@/lib/product/strapCta';
import { devLog, devWarn } from '@/lib/utils/devLog';
import { formatPrice } from '@/lib/utils/formatPrice';
import { cache } from 'react';

import type { FetchListingPageOptions, HeroSettings, ListingPageData } from './types';

import { extractAlgoliaSettings, extractHeroFromContent, extractSEOConfig } from './helpers';
import { getListingCardMaterial, getListingCardSubtitle } from './productCardFields';
import { PLP_CONFIGS, resolveStrapMaterialSlug } from './plpConfig';

type ListingProductKind = 'Watch' | 'Strap' | 'Eyewear';

function toListingProductKind(productType?: string): ListingProductKind {
  if (productType === 'Strap') return 'Strap';
  if (productType === 'Eyewear') return 'Eyewear';
  return 'Watch';
}

function buildListingCustomFilter(params: {
  baseFilters?: string;
  locale: string;
  collectionName?: string;
  type: string;
  algoliaCategoryId?: string | null;
}): string {
  let customFilter = params.baseFilters || '';

  if (params.algoliaCategoryId) {
    const categoryFilter = `category_page_id:'${params.algoliaCategoryId}'`;
    customFilter = customFilter ? `${customFilter} AND ${categoryFilter}` : categoryFilter;
    devLog('[ListingOrchestrator] Using CMS category_page_id filter:', categoryFilter);
  } else if (params.collectionName && params.type === 'watch collection') {
    const collectionFilter = `collection:'${params.collectionName}'`;
    customFilter = customFilter ? `${customFilter} AND ${collectionFilter}` : collectionFilter;
    devLog('[ListingOrchestrator] Fallback to collection filter:', collectionFilter);
  }

  return customFilter;
}

async function enrichStrapListingProducts(
  products: ProductCardData[],
  strapHits: StrapHit[],
  locale: string
): Promise<ProductCardData[]> {
  const groupSkus = products.flatMap((product) => {
    const hit = strapHits.find((h) => h.objectID === product.sku);
    return hit?.variant_group?.length ? hit.variant_group : [product.sku];
  });

  const variantMap = await fetchStrapVariantCtas(locale, groupSkus);

  return products.map((card) => {
    const hit = strapHits.find((h) => h.objectID === card.sku);
    if (!hit) return card;

    const { displayCta, addCta, addSku, addVariantId } = resolveStrapListingCta({
      hitCta: hit.call_to_action,
      variantGroup: hit.variant_group,
      variantMap,
      hitSku: hit.objectID,
    });

    return {
      ...card,
      ctaType: displayCta ?? card.ctaType,
      addCtaType: addCta ?? displayCta ?? card.ctaType,
      addSku,
      addVariantId,
      integratedBuckle: hit.integrated_buckle ?? card.integratedBuckle,
    };
  });
}

async function fetchListingConditionalData(
  content: CMSContentItem[],
  locale: string,
  translationFn: (key: string) => string,
  placeholderStores: CMSPlaceholderStores | null
): Promise<{ stores?: Awaited<ReturnType<typeof fetchStores>> }> {
  const conditionalFetches: Promise<unknown>[] = [];
  let stores: Awaited<ReturnType<typeof fetchStores>> | undefined;

  if (placeholderStores) {
    const [country] = locale.split('-');
    const storesFetch = fetchStores({
      countryCode: country.toUpperCase(),
      limit: placeholderStores.numberOfStores || 3,
      mode: placeholderStores.type || 'Default',
      storeIds: placeholderStores.boutiqueReferenceKeys?.length
        ? placeholderStores.boutiqueReferenceKeys
        : undefined,
      eyewearAvailable: placeholderStores.eyewearAvailable ?? undefined,
    }).then((result) => {
      stores = result;
      devLog('[ListingOrchestrator] Fetched stores:', {
        count: stores.length,
        country: country.toUpperCase(),
      });
    });
    conditionalFetches.push(storesFetch);
  }

  conditionalFetches.push(
    populateSliderProducts(content, locale, translationFn),
    populateCollectionGroupProducts(content, locale, translationFn)
  );
  await Promise.all(conditionalFetches);

  return { stores };
}

/**
 * Find PlaceholderStores or FindAStore component in content
 * Both types use the PlaceholderStores component for rendering
 */
function findPlaceholderStores(content: CMSContentItem[]): CMSPlaceholderStores | null {
  const found = content.find(
    (item) => item.__typename === 'PlaceholderStores' || item.__typename === 'FindAStore'
  );
  return found ? (found as CMSPlaceholderStores) : null;
}

/**
 * Find product sliders that need SKU-based data fetching.
 * SliderProductsManual components contain an array of SKUs that need to be
 * resolved to full product data via Algolia.
 *
 * This function recursively searches inside Grid content collections to find
 * nested sliders (e.g., homepage has sliders inside Grid items).
 */
function findProductSlidersWithSkus(content: CMSContentItem[]): CMSTrendingProducts[] {
  const sliders: CMSTrendingProducts[] = [];

  for (const item of content) {
    // Check if this item is a slider with SKUs
    if (
      item.__typename === 'SliderProductsManual' ||
      item.__typename === 'SliderProductsAutomatic'
    ) {
      const slider = item as CMSTrendingProducts;
      if (Array.isArray(slider.productSkus) && slider.productSkus.length > 0) {
        sliders.push(slider);
      }
    }

    // Recursively check inside Grid content collections
    if (item.__typename === 'Grid') {
      const grid = item as { contentCollection?: { items?: CMSContentItem[] } };
      if (grid.contentCollection?.items) {
        const nestedSliders = findProductSlidersWithSkus(grid.contentCollection.items);
        sliders.push(...nestedSliders);
      }
    }
  }

  return sliders;
}

/**
 * Collect all unique SKUs from product sliders
 */
function collectSliderSkus(sliders: CMSTrendingProducts[]): string[] {
  const skuSet = new Set<string>();
  for (const slider of sliders) {
    if (slider.productSkus) {
      for (const sku of slider.productSkus) {
        if (sku) skuSet.add(sku);
      }
    }
  }
  return Array.from(skuSet);
}

/**
 * Transform Algolia product hits to CMSProductCard format for sliders
 */
function transformHitToProductCard(
  hit: Record<string, unknown>,
  locale: string,
  badgeTranslations: (key: string) => string
): CMSProductCard {
  const localeCode = getLocaleCode(locale);
  const [country, language] = locale.split('-');
  const _intlLocale = `${language}-${country.toUpperCase()}`;

  // Get localized name
  const nameObj = hit.name as Record<string, string> | undefined;
  const name = nameObj?.[localeCode] || nameObj?.['EN_US'] || (hit.objectID as string);

  // Get collection name for subtitle
  const collections = hit.collections as Array<{ name?: string; slug?: string }> | undefined;
  const collection = collections?.[0]?.name || (hit.collection as string) || '';

  // Build PDP URL using shared utility
  const collectionSlug = collections?.[0]?.slug || 'collection';
  const model = hit.model as { slug?: string } | undefined;
  const modelSlug = model?.slug || 'model';
  const pdpUrl = buildWatchUrl(collectionSlug, modelSlug, hit.objectID as string);

  // Format price using locale-aware formatting with currency codes
  const price = hit.price as number | undefined;
  const currency = (hit.currency as string) || 'USD';
  const formattedPrice = price && price > 0 ? formatPrice(price, currency, locale) : undefined;

  // Get product badge
  const marketingStatus = hit.marketing_status;
  const statusArray = marketingStatus
    ? ((Array.isArray(marketingStatus) ? marketingStatus : [marketingStatus]) as string[])
    : undefined;
  const badge = getProductBadge(statusArray, hit.launch_date as string | undefined, {
    isPDP: false,
  });

  // Get case material (locale-specific)
  const caseObj = hit.case as Record<string, string> | undefined;
  const material = caseObj?.[localeCode] || caseObj?.['EN_US'] || undefined;

  const rawProductType = hit.product_type as string | undefined;
  const productType =
    rawProductType === 'Strap' || rawProductType === 'strap'
      ? ('Strap' as const)
      : rawProductType === 'Eyewear' || rawProductType === 'eyewear'
        ? ('Eyewear' as const)
        : ('Watch' as const);

  return {
    sys: { id: hit.objectID as string },
    name,
    subtitle: collection,
    sku: hit.objectID as string,
    price: price ?? null,
    currency: currency,
    formattedPrice: formattedPrice ?? null,
    image: {
      url:
        (hit.thumbnail_url as string) ||
        (hit.image_url as string) ||
        getProductPlaceholder('watch'),
      alt: name,
      width: null,
      height: null,
    },
    href: pdpUrl,
    label: badge ? badgeTranslations(badge.labelKey) : null,
    // Breitling ProductCard fields
    variantId: (hit.variant_id as string | undefined) ?? null,
    ctaType: (hit.call_to_action as string | undefined) ?? null,
    material: material ?? null,
    productType,
  };
}

/**
 * Populate product sliders with actual product data from Algolia.
 * This mutates the content array in place for efficiency.
 *
 * Exported for use by homepage and other page routes that need slider population.
 */
export async function populateSliderProducts(
  content: CMSContentItem[],
  locale: string,
  badgeTranslations: (key: string) => string
): Promise<void> {
  const sliders = findProductSlidersWithSkus(content);

  if (sliders.length === 0) {
    return;
  }

  // Collect all unique SKUs across all sliders
  const allSkus = collectSliderSkus(sliders);

  if (allSkus.length === 0) {
    return;
  }

  devLog('[ListingOrchestrator] Fetching product data for slider SKUs:', {
    sliderCount: sliders.length,
    skuCount: allSkus.length,
    skus: allSkus,
  });

  // Fetch all products in a single batch request (doesn't count against search quota)
  const products = await getProductsByIds(locale, allSkus);

  // Create a map of SKU -> product data
  const productMap = new Map<string, CMSProductCard>();
  for (let i = 0; i < allSkus.length; i++) {
    const hit = products[i];
    if (hit) {
      const productCard = transformHitToProductCard(
        hit as unknown as Record<string, unknown>,
        locale,
        badgeTranslations
      );
      productMap.set(allSkus[i], productCard);
    }
  }

  devLog('[ListingOrchestrator] Resolved products from Algolia:', {
    requested: allSkus.length,
    found: productMap.size,
  });

  // Populate each slider with its products in order
  for (const slider of sliders) {
    if (slider.productSkus) {
      slider.products = slider.productSkus
        .map((sku) => productMap.get(sku))
        .filter((p): p is CMSProductCard => p != null);

      devLog('[ListingOrchestrator] Populated slider:', {
        typename: slider.__typename,
        id: slider.sys?.id,
        requestedSkus: slider.productSkus.length,
        populatedProducts: slider.products.length,
      });
    }
  }
}

/**
 * Find CollectionGroup components that need product data
 */
function findCollectionGroups(content: CMSContentItem[]): CMSCollectionGroup[] {
  return content.filter((item): item is CMSCollectionGroup => {
    return item.__typename === 'CollectionGroup';
  });
}

/**
 * Populate CollectionGroup components with product data from Algolia.
 *
 * Handles two types of product references:
 * - AutomaticCollectionGroup: Fetch by collection name
 * - ManualCollectionGroupProductReferences: Fetch by SKUs
 */
export async function populateCollectionGroupProducts(
  content: CMSContentItem[],
  locale: string,
  badgeTranslations: (key: string) => string
): Promise<void> {
  const collectionGroups = findCollectionGroups(content);

  if (collectionGroups.length === 0) {
    return;
  }

  devLog('[ListingOrchestrator] Processing CollectionGroup components:', {
    count: collectionGroups.length,
  });

  // Process each collection group
  for (const group of collectionGroups) {
    // Manual SKUs take priority
    if (group.productSkus && group.productSkus.length > 0) {
      devLog('[ListingOrchestrator] Fetching manual products for CollectionGroup:', {
        id: group.sys?.id,
        skuCount: group.productSkus.length,
      });

      const products = await getProductsByIds(locale, group.productSkus);
      group.products = group.productSkus
        .map((sku, i) => {
          const hit = products[i];
          if (!hit) return null;
          return transformHitToProductCard(
            hit as unknown as Record<string, unknown>,
            locale,
            badgeTranslations
          );
        })
        .filter((p): p is CMSProductCard => p != null);
    } else if (group.collectionName) {
      // Fetch by collection name using Algolia search
      devLog('[ListingOrchestrator] Fetching collection products for CollectionGroup:', {
        id: group.sys?.id,
        collection: group.collectionName,
        maxProducts: group.maxProducts,
      });

      try {
        // Build collection filter
        const searchResult = await searchProductsInternal({
          locale,
          filters: {
            collection: [group.collectionName],
            page: 1,
          },
          hitsPerPage: group.maxProducts || 10,
        });

        group.products = searchResult.hits.map((hit) =>
          transformHitToProductCard(
            hit as unknown as Record<string, unknown>,
            locale,
            badgeTranslations
          )
        );

        devLog('[ListingOrchestrator] Populated CollectionGroup:', {
          id: group.sys?.id,
          collection: group.collectionName,
          productCount: group.products.length,
        });
      } catch (error) {
        devWarn('[ListingOrchestrator] Failed to fetch collection products:', {
          id: group.sys?.id,
          collection: group.collectionName,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
        group.products = [];
      }
    } else {
      devWarn('[ListingOrchestrator] CollectionGroup has no product source:', {
        id: group.sys?.id,
      });
      group.products = [];
    }
  }
}

/**
 * Transform Algolia hits to unified ProductCardData format.
 * Handles different product types (Watch, Strap, Eyewear) with appropriate
 * field mappings and URL structures.
 */
async function transformAlgoliaHits(
  hits: Array<Record<string, unknown>>,
  locale: string,
  badgeTranslations: (key: string) => string,
  productType: 'Watch' | 'Strap' | 'Eyewear' = 'Watch'
): Promise<ProductCardData[]> {
  const localeCode = getLocaleCode(locale);

  return hits.map((hit) => {
    // Get localized name - structure varies by product type
    const nameObj = hit.name as Record<string, string> | undefined;
    const name = nameObj?.[localeCode] || nameObj?.['EN_US'] || (hit.objectID as string);
    const subtitle = getListingCardSubtitle(hit, productType, localeCode);

    // Build PDP URL based on product type. Locale-relative; the consuming
    // i18n <Link> adds the locale (see comment on buildWatchUrl).
    let pdpUrl: string;
    if (productType === 'Strap') {
      // Straps: /straps/{material}/{sku}
      const materialSlug = resolveStrapMaterialSlug(
        {
          material_en: hit.material_en as string | undefined,
          material: hit.material as Record<string, string> | undefined,
        },
        localeCode
      );
      pdpUrl = buildStrapUrl(materialSlug, hit.objectID as string);
    } else if (productType === 'Eyewear') {
      // Eyewear: /sunglasses/{sku}
      pdpUrl = `/sunglasses/${hit.objectID}`;
    } else {
      // Watches: /watches/{collection}/{model}/{sku}
      const collections = hit.collections as Array<{ name?: string; slug?: string }> | undefined;
      const model = hit.model as { slug?: string } | undefined;
      pdpUrl = buildWatchUrl(
        collections?.[0]?.slug || 'collection',
        model?.slug || 'model',
        hit.objectID as string
      );
    }

    // Format price using locale-aware formatting with currency codes
    const price = hit.price as number | undefined;
    const currency = (hit.currency as string) || 'USD';
    const formattedPrice = price && price > 0 ? formatPrice(price, currency, locale) : undefined;

    // Get product badge/label based on marketing status and launch date
    const marketingStatus = hit.marketing_status;
    const statusArray = marketingStatus
      ? ((Array.isArray(marketingStatus) ? marketingStatus : [marketingStatus]) as string[])
      : undefined;
    const badge = getProductBadge(statusArray, hit.launch_date as string | undefined, {
      isPDP: false,
    });

    const material = getListingCardMaterial(hit, productType, localeCode);

    // Get dial color for watches (used in cart variant display: "Material - Dial Color")
    let dialColor: string | undefined;
    if (productType === 'Watch') {
      const dialColorObj = hit.dial_color as Record<string, string> | undefined;
      dialColor = dialColorObj?.[localeCode] || dialColorObj?.['EN_US'] || undefined;
    }

    // TODO: warrantyExtensionDuration is not currently indexed in Algolia.
    // It's only available from Saleor (wmo-warranty-extension-duration attribute).
    // To enable optimistic warranty extension button display on PLP, add
    // warranty_extension_duration to the Algolia index, then extract it here:
    // const warrantyExtensionDuration = (hit.warranty_extension_duration as string) || undefined;

    return {
      id: hit.objectID as string,
      sku: hit.objectID as string,
      name,
      subtitle,
      price: formattedPrice ?? '',
      priceAmount: price,
      currency,
      image:
        (hit.thumbnail_url as string) ||
        (hit.image_url as string) ||
        getProductPlaceholder(productType),
      href: pdpUrl,
      variantId: hit.variant_id as string | undefined,
      ctaType: hit.call_to_action as string | undefined,
      label: badge ? badgeTranslations(badge.labelKey) : undefined,
      material,
      dialColor,
      productType,
    };
  });
}

/**
 * Fetch all listing page data
 *
 * Orchestrates calls to multiple services in optimized parallel batches:
 *
 * Batch 1 (parallel):
 * - Contentful ListingPage (CMS content, SEO, hero settings)
 * - Algolia product search
 * - Algolia all facets (for filter options)
 * - Translations
 *
 * @param options - Fetch options including type, locale, filters, etc.
 * @returns Complete listing page data
 */
export const fetchListingPageData = cache(
  async (options: FetchListingPageOptions): Promise<ListingPageData> => {
    return runWithAlgoliaDebugCollection(async () => {
      const {
        type,
        locale,
        filters,
        collectionName,
        slug,
        draftMode = false,
        badgeTranslations,
        baseFilters,
        productType,
      } = options;

      devLog('[ListingOrchestrator] Fetching listing page data:', {
        type,
        locale,
        collectionName,
        slug,
        baseFilters,
        productType,
      });

      // ============================================
      // Batch 1: All data sources (parallel)
      // NOTE: getTranslations is NOT called here - it must be passed in from the page
      // component to avoid DYNAMIC_SERVER_USAGE errors when used inside cache()
      // ============================================

      // First, fetch CMS data to get algoliaCategoryId and algoliaRuleName
      // This needs to happen before the product search for editorial pages
      const listingPageResult = await fetchListingPage({
        type,
        slug,
        collectionName,
        locale,
        preview: draftMode,
      });

      // Extract Algolia settings from CMS
      const listingPage = listingPageResult?.items?.[0] || null;
      const cmsAlgoliaSettings = extractAlgoliaSettings(listingPage);

      // Merge base filters into the filter state
      // CMS algoliaCategoryId takes priority - it's the source of truth for what products to show
      const customFilter = buildListingCustomFilter({
        baseFilters,
        locale,
        collectionName,
        type,
        algoliaCategoryId: cmsAlgoliaSettings.algoliaCategoryId,
      });

      const filtersWithBase = {
        ...filters,
        ...(customFilter && { customFilter }),
        // Pass algoliaRuleName as query string to trigger Algolia rules
        ...(cmsAlgoliaSettings.algoliaRuleName && {
          algoliaRuleName: cmsAlgoliaSettings.algoliaRuleName,
        }),
      };

      devLog('[ListingOrchestrator] Filters with CMS settings:', {
        baseFilters,
        algoliaCategoryId: cmsAlgoliaSettings.algoliaCategoryId,
        algoliaRuleName: cmsAlgoliaSettings.algoliaRuleName,
        customFilter,
      });

      const listingProductKind = toListingProductKind(productType);
      const searchProductType: SearchProductType = listingProductKind;
      const facetProductType: FacetProductType = listingProductKind;

      // Fetch facets first (ISR cached - essentially instant on cache hit)
      // This allows us to pass them to the search function, avoiding a double fetch
      const allFacets = await getCachedFacetsDeduped(locale, facetProductType);

      // Now search with pre-fetched facets (no extra /api/facets call needed)
      const searchResult = await searchByProductType({
        locale,
        filters: filtersWithBase,
        draftMode,
        productType: searchProductType,
        // Pass pre-fetched facets to avoid double fetch in search function
        facetLookup: allFacets || undefined,
      });

      // Get content items from Contentful (listingPage was extracted earlier)
      const rawContent = filterContent(listingPage?.contentCollection?.items) as CMSContentItem[];

      // Extract HeroProductListing from content (if present)
      const { heroSettings, content } = extractHeroFromContent(rawContent);

      // Build SEO variables for placeholder replacement
      const seoVariables = {
        collectionName: collectionName || null,
        strapMaterial: slug || null, // For strap material pages, slug is the material name
        locale,
      };

      // Extract SEO config with variable replacement
      const seoConfig = extractSEOConfig(listingPage, seoVariables);

      // Note: algoliaSettings already extracted above before search

      // Log hero extraction result
      if (heroSettings) {
        devLog('[ListingOrchestrator] Extracted HeroProductListing settings:', {
          showFilters: heroSettings.showFilters,
          showBreadcrumbs: heroSettings.showBreadcrumbs,
          hasMedia: !!heroSettings.media,
        });
      } else {
        devLog('[ListingOrchestrator] No HeroProductListing found in content');
      }

      // Transform Algolia hits to ProductCardData
      // Use provided badgeTranslations or fallback to returning the key
      const translationFn = badgeTranslations || ((key: string) => key);

      const transformProductType = listingProductKind;

      let products = await transformAlgoliaHits(
        searchResult.hits as unknown as Array<Record<string, unknown>>,
        locale,
        translationFn,
        transformProductType
      );

      const plpConfig = Object.values(PLP_CONFIGS).find((config) => config.cmsType === type);
      if (transformProductType === 'Strap' && plpConfig?.resolveMostRelevantCta) {
        products = await enrichStrapListingProducts(
          products,
          searchResult.hits as StrapHit[],
          locale
        );
      }

      // ============================================
      // Batch 2: Conditional data (based on CMS content)
      // Fetch additional data for CMS components that reference external data:
      // - PlaceholderStores: fetch store data from Algolia
      // - SliderProductsManual: fetch product data for SKUs from Algolia
      // - CollectionGroup: fetch products by collection or SKUs from Algolia
      // ============================================

      const placeholderStores = findPlaceholderStores(content);
      const { stores } = await fetchListingConditionalData(
        content,
        locale,
        translationFn,
        placeholderStores
      );

      devLog('[ListingOrchestrator] Data fetched:', {
        contentItems: content.length,
        products: products.length,
        totalHits: searchResult.totalHits,
        hasHero: !!heroSettings,
        hasStores: !!stores,
      });

      return {
        content,
        seoConfig,
        heroSettings,
        products,
        facets: searchResult.facets || {},
        allFacets: allFacets || null,
        totalHits: searchResult.totalHits,
        pagination: {
          currentPage: searchResult.page,
          totalPages: searchResult.totalPages,
        },
        stores,
        algoliaDebug: getAlgoliaDebugEntries(),
        ...cmsAlgoliaSettings,
      };
    });
  }
);

/**
 * Get default hero settings when no HeroProductListing is in CMS
 * This provides sensible defaults for the filter bar and breadcrumbs
 */
export function getDefaultHeroSettings(): HeroSettings {
  return {
    heading: null,
    subheading: null,
    textAlignment: 'center',
    headingNumberOfColoredWords: null,
    headingSeoTag: 'h1',
    headingStyle: null,
    subheaderIsCapitalized: false,
    media: null,
    backgroundColor: null,
    backgroundColorOpacity: null,
    textColor: null,
    textBelowMedia: false,
    showFilters: true,
    showBreadcrumbs: true,
  };
}
